import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  RefreshCcw,
  Copy,
  Phone,
  Mail,
  Search,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fynix_admin_pw";

export default function AdminLeads() {
  const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : "";
  const [pw, setPw] = useState(stored || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!stored);
  const [loginLoading, setLoginLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  // Validate stored password on mount
  useEffect(() => {
    if (!stored) return;
    (async () => {
      try {
        await axios.get(`${API}/admin/session`, {
          headers: { "X-Admin-Password": stored },
        });
        setAuthed(true);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setPw("");
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    })();
  }, [stored]);

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  const headers = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);

  async function refresh() {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads?limit=500`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(l.data || []);
      setStats(s.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired", { description: "Please sign in again." });
        handleLogout();
      } else {
        toast.error("Could not load leads", {
          description: String(err?.message || err),
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (!pw.trim()) return;
    setLoginLoading(true);
    try {
      await axios.get(`${API}/admin/session`, {
        headers: { "X-Admin-Password": pw },
      });
      localStorage.setItem(STORAGE_KEY, pw);
      setAuthed(true);
      toast.success("Signed in");
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoginLoading(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem(STORAGE_KEY);
    setPw("");
    setAuthed(false);
    setLeads([]);
    setStats(null);
  }

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return leads;
    return leads.filter((l) =>
      [l.name, l.business_name, l.email, l.phone, l.business_type, l.biggest_bottleneck]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [leads, q]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!authed) {
    return <LoginScreen pw={pw} setPw={setPw} onSubmit={handleLogin} loading={loginLoading} />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="admin-leads-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-950 transition-colors"
              data-testid="admin-back-site"
            >
              <ArrowLeft size={14} /> Back to site
            </Link>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-600">
              Admin · Leads
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-white text-zinc-900 px-3 py-2 text-sm border border-zinc-300 hover:border-zinc-950 transition-colors disabled:opacity-60"
              data-testid="admin-refresh"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCcw size={14} />
              )}
              Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-3 py-2 text-sm border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(234,88,12,1)] transition-all"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-8">
            <Stat label="Total leads" value={stats.total} />
            <Stat label="Last 24 hours" value={stats.last_24h} />
            <Stat label="Last 7 days" value={stats.last_7d} />
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, bottleneck…"
              className="w-full bg-white border border-zinc-300 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
              data-testid="admin-search"
            />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            {filtered.length} / {leads.length}
          </span>
        </div>

        {/* Table */}
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="text-left border-b border-zinc-200 bg-[#FAFAFA]">
                <Th>When</Th>
                <Th>Name</Th>
                <Th>Business</Th>
                <Th>Contact</Th>
                <Th>Type</Th>
                <Th>Bottleneck</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-zinc-500">
                    {leads.length === 0
                      ? "No leads yet. When someone submits the form, they'll appear here."
                      : "No leads match your search."}
                  </td>
                </tr>
              ) : (
                filtered.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className="border-b border-zinc-100 hover:bg-[#FAFAFA] cursor-pointer"
                    data-testid={`admin-lead-row-${l.id}`}
                  >
                    <td className="p-3 font-mono text-[11px] text-zinc-500 whitespace-nowrap align-top">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="p-3 font-medium align-top">{l.name}</td>
                    <td className="p-3 text-zinc-700 align-top">{l.business_name}</td>
                    <td className="p-3 align-top">
                      <div className="text-zinc-950">{l.email}</div>
                      <div className="text-zinc-500 text-xs font-mono">{l.phone}</div>
                    </td>
                    <td className="p-3 align-top">
                      <span className="inline-block border border-zinc-300 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-zinc-700">
                        {l.business_type}
                      </span>
                    </td>
                    <td className="p-3 text-zinc-700 align-top max-w-sm">
                      <div className="line-clamp-2">{l.biggest_bottleneck}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Detail Drawer */}
      {selected && <DetailDrawer lead={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function LoginScreen({ pw, setPw, onSubmit, loading }) {
  return (
    <div
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white border border-zinc-950 p-8 sm:p-10 shadow-[10px_10px_0px_0px_rgba(234,88,12,1)]"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 bg-zinc-950 text-white">
            <Lock size={16} />
          </span>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-600">
              Admin Access
            </div>
            <h1 className="font-display font-bold text-xl tracking-tight text-zinc-950">
              Fynix Systems — Leads
            </h1>
          </div>
        </div>

        <label
          htmlFor="admin-pw"
          className="mt-8 block font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500"
        >
          Admin password
        </label>
        <input
          id="admin-pw"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          placeholder="••••••••••••"
          className="mt-2 w-full bg-white border border-zinc-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
          data-testid="admin-pw-input"
        />

        <button
          type="submit"
          disabled={loading || !pw}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm font-medium border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(234,88,12,1)] transition-all disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
          Sign in
        </button>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-950 transition-colors"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
      </form>
    </div>
  );
}

function DetailDrawer({ lead, onClose }) {
  async function copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex"
      data-testid="admin-lead-drawer"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="flex-1 bg-zinc-950/40"
      />
      <aside className="w-full max-w-md bg-white border-l border-zinc-950 overflow-y-auto shadow-[0_0_40px_rgba(10,10,10,0.25)]">
        <div className="sticky top-0 bg-zinc-950 text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-500">
              Lead · {lead.id.slice(0, 8)}
            </div>
            <h3 className="mt-1 font-display font-bold text-xl tracking-tight">
              {lead.name}
            </h3>
            <p className="text-sm text-zinc-400">{lead.business_name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-xs text-zinc-400 hover:text-white"
          >
            Close ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Row label="Email">
            <a
              href={`mailto:${lead.email}`}
              className="text-zinc-950 hover:text-orange-600 inline-flex items-center gap-2"
            >
              <Mail size={14} /> {lead.email}
            </a>
            <IconBtn onClick={() => copy(lead.email)}><Copy size={12} /></IconBtn>
          </Row>
          <Row label="Phone">
            <a
              href={`tel:${lead.phone}`}
              className="text-zinc-950 hover:text-orange-600 inline-flex items-center gap-2"
            >
              <Phone size={14} /> {lead.phone}
            </a>
            <IconBtn onClick={() => copy(lead.phone)}><Copy size={12} /></IconBtn>
          </Row>
          <Row label="Business type">
            <span>{lead.business_type}</span>
          </Row>
          <Row label="Submitted">
            <span className="font-mono text-xs text-zinc-600">
              {formatDate(lead.created_at, true)}
            </span>
          </Row>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Biggest bottleneck
            </div>
            <p className="text-zinc-900 leading-relaxed bg-[#FAFAFA] border border-zinc-200 p-4 whitespace-pre-wrap">
              {lead.biggest_bottleneck}
            </p>
          </div>

          {(lead.ip || lead.user_agent) && (
            <div className="pt-4 border-t border-zinc-200">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
                Diagnostics
              </div>
              {lead.ip && (
                <div className="text-xs text-zinc-600 font-mono">IP · {lead.ip}</div>
              )}
              {lead.user_agent && (
                <div className="text-xs text-zinc-600 font-mono break-all mt-1">
                  UA · {lead.user_agent}
                </div>
              )}
            </div>
          )}

          <div className="pt-4 flex flex-col sm:flex-row gap-2">
            <a
              href={`mailto:${lead.email}?subject=Your%20AI%20Bottleneck%20Assessment%20with%20Fynix%20Systems`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(234,88,12,1)] transition-all"
            >
              <Mail size={14} /> Reply
            </a>
            <a
              href={`tel:${lead.phone}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-zinc-950 px-4 py-2.5 text-sm font-medium border border-zinc-300 hover:border-zinc-950 transition-colors"
            >
              <Phone size={14} /> Call
            </a>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white p-6">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </div>
      <div className="mt-2 font-display font-bold text-3xl text-zinc-950 tracking-tight tabular-nums">
        {value}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
      {children}
    </th>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className="flex items-center gap-2 text-sm">{children}</div>
    </div>
  );
}

function IconBtn({ children, ...rest }) {
  return (
    <button
      type="button"
      className="p-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
      {...rest}
    >
      {children}
    </button>
  );
}

function formatDate(iso, withSeconds = false) {
  try {
    const d = new Date(iso);
    const opts = withSeconds
      ? { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }
      : { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return d.toLocaleString(undefined, opts);
  } catch {
    return iso;
  }
}
